import { useParams } from "react-router-dom";
import { db, auth } from "../firebase";
import {
  createContext,
  useReducer,
  useEffect,
  useState,
  useCallback,
  useContext,
} from "react";
import {
  addPage,
  deletePage,
  addComponent,
  updateComponent,
  deleteComponent,
  addPlugin,
  updatePluginConfig,
  updatePluginVersion,
  addRole,
  deleteRole,
  updateRoleName,
} from "../utils/appactions";
import { useHistory } from "react-router-dom";
import { LoaderDisbatch } from "./loader";
export const AppValue = createContext(null);
export const AppDisbatch = createContext(null);

const reducer = (state, action) => {
  const newState = { ...state };
  if (action.type === "set") {
    return action.value;
  }
  if (action.type === "addPlugin") {
    const newApp = addPlugin(
      newState,
      action.plugin,
      action.id,
      action.version,
      action.config
    );
    return newApp;
  }
  if (action.type === "updatePluginConfig") {
    const newApp = updatePluginConfig(newState, action.plugin, action.value);
    return newApp;
  }
  if (action.type === "updatePluginVersion") {
    const newApp = updatePluginVersion(newState, action.plugin, action.value);
    return newApp;
  }
  if (action.type === "addRole") {
    const newApp = addRole(newState, action.id, action.name);
    return newApp;
  }
  if (action.type === "deleteRole") {
    const newApp = deleteRole(newState, action.role);
    return newApp;
  }
  if (action.type === "updateRoleName") {
    const newApp = updateRoleName(newState, action.id, action.name);
    return newApp;
  }

  return newState;
};

export default function AppContext({ children }) {
  const history = useHistory();
  const setLoader = useContext(LoaderDisbatch);
  const [state, dispatch] = useReducer(reducer, {
    name: "",
    roles: {},
    members: {},
  });
  const { appId, pageId, type } = useParams();
  const [user, setUser] = useState(null);
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState({});
  const [components, setComponents] = useState([]);

  const _appActions = useCallback(
    (action) => {
      if (action.type === "addPage") {
        setLoader(true);
        addPage(appId, action.name).then((pageId) => {
          history.push(`/${appId}/canvas/pages/${pageId}`);
          setLoader(false);
        });
        return;
      }
      if (action.type === "setPages") {
        const _pages = action.value.sort((x, y) => {
          if (x.order > y.order && x.topLevel && y.topLevel) {
            return 1;
          } else if (x.topLevel && y.topLevel) {
            return -1;
          } else {
            return -10;
          }
        });
        setPages(_pages);
        const batch = db.batch();
        _pages.forEach((p) => {
          batch.set(
            db.collection("apps").doc(appId).collection("pages").doc(p.id),
            p
          );
        });
        batch.commit();
        return;
      }
      if (action.type === "setPage") {
        const page = action.value;
        db.collection("apps")
          .doc(appId)
          .collection("pages")
          .doc(page.id)
          .set(page);
        return;
      }
      if (action.type === "deletePage") {
        setLoader(true);
        db.collection("apps")
          .doc(appId)
          .collection("pages")
          .doc(action.value.id)
          .delete()
          .then(() => {
            history.push(`/${appId}/canvas/pages/index`);
            setLoader(false);
          })
          .catch(() => {
            history.push(`/${appId}/canvas/pages/index`);
            setLoader(false);
          });
      }
      if (action.type === "addComponent") {
        setLoader(true);
        return addComponent(appId, action.name).then((compId) => {
          setTimeout(() => {
            setLoader(false);
            history.push(`/${appId}/canvas/components/${compId}`);
          }, 1500);
        });
      }
      if (action.type === "updateComponent") {
        return updateComponent(appId, action.component);
      }
      if (action.type === "deleteComponent") {
        setLoader(true);
        return deleteComponent(appId, action.id).then(() => {
          history.push(`/${appId}/canvas/pages/index`);
          setLoader(false);
        });
      }
      dispatch(action);
    },
    [appId]
  );

  useEffect(() => {
    if (appId && pageId) {
      db.collection("apps")
        .doc(appId)
        .onSnapshot((doc) => {
          dispatch({ type: "set", value: { ...doc.data(), id: doc.id } });
        });
    }
  }, [appId, pageId]);

  useEffect(() => {
    if (appId && pageId) {
      db.collection("apps")
        .doc(appId)
        .collection("pages")
        //.orderBy("order", "asc")
        .onSnapshot((snap) => {
          const pages = snap.docs.map((doc) => {
            return { ...doc.data(), id: doc.id };
          });
          setPages(pages);
        });
    }
  }, [appId, pageId]);

  useEffect(() => {
    if (pageId && type === "pages") {
      const page = pages.find((x) => x.id === pageId);
      if (page) {
        setCurrentPage(page);
      } else {
        setCurrentPage(null);
      }
    } else {
      setCurrentPage(null);
    }
  }, [pageId, type, pages]);

  useEffect(() => {
    if (appId && pageId) {
      db.collection("apps")
        .doc(appId)
        .collection("components")
        .onSnapshot((snap) => {
          const components = snap.docs.map((doc) => {
            return { ...doc.data(), id: doc.id };
          });
          setComponents(components);
        });
    }
  }, [appId, pageId]);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => {
      if (!u) {
        history.push("/auth/login");
      } else {
        setUser(u);
      }
    });
    return unsub;
  }, []);

  if (!user) {
    return null;
  }

  return (
    <AppValue.Provider value={{ ...state, pages, currentPage, components }}>
      <AppDisbatch.Provider value={_appActions}>
        {children}
      </AppDisbatch.Provider>
    </AppValue.Provider>
  );
}
